// @flow

/**
 * Pricing utility functions for handling Stripe tiered pricing.
 * Extracted from SubscribeHome.js for reuse in BillingContent.js
 */

export type TierBreakdown = {
  seats: number,
  pricePerSeat: ?number,
  subtotal: number,
  isFlat: boolean
};

/**
 * Calculate total price for tiered pricing.
 * Supports both 'volume' and 'graduated' tiers_mode.
 * Handles flat_amount, unit_amount, or both combined per tier.
 *
 * @param tiers - Array of Stripe tier objects
 * @param seatCount - Number of seats/units
 * @param tiersMode - 'volume' or 'graduated' (default)
 * @returns Total price in dollars (not cents)
 */
export const calculateTieredPrice = (tiers: ?Array<Object>, seatCount: number, tiersMode: ?string): number => {
  if (!tiers || tiers.length === 0 || seatCount <= 0) return 0;

  // Volume-based: entire quantity charged at the tier that matches the total quantity
  if (tiersMode === 'volume') {
    for (const tier of tiers) {
      if (tier.up_to === null || seatCount <= tier.up_to) {
        const flatAmount = tier.flat_amount || 0;
        const unitTotal = (tier.unit_amount || 0) * seatCount;
        return (flatAmount + unitTotal) / 100;
      }
    }
    return 0;
  }

  // Graduated (default): each unit charged at its tier's rate
  let total = 0;
  let remaining = seatCount;
  let prevUpTo = 0;

  for (const tier of tiers) {
    const tierMax = tier.up_to === null ? remaining : tier.up_to - prevUpTo;
    const unitsInTier = Math.min(remaining, tierMax);
    const flatAmount = tier.flat_amount || 0;
    const unitAmount = tier.unit_amount || 0;
    // For graduated mode, flat_amount applies once per tier, unit_amount per unit
    total += flatAmount + (unitsInTier * unitAmount);
    remaining -= unitsInTier;
    prevUpTo = tier.up_to || 0;
    if (remaining <= 0) break;
  }

  return total / 100;
};

/**
 * Get current per-seat price for tiered pricing.
 * Returns null for flat_amount-only tiers (no per-seat price to display).
 * Returns the per-seat price for unit_amount tiers or combined tiers.
 *
 * @param tiers - Array of Stripe tier objects
 * @param seatCount - Number of seats/units
 * @returns Per-seat price in dollars, or null if flat pricing
 */
export const getCurrentTierPrice = (tiers: ?Array<Object>, seatCount: number): ?number => {
  if (!tiers || tiers.length === 0 || seatCount <= 0) return null;
  for (const tier of tiers) {
    if (tier.up_to === null || seatCount <= tier.up_to) {
      const flatAmount = tier.flat_amount || 0;
      const unitAmount = tier.unit_amount || 0;
      // If tier has flat_amount but no unit_amount, return null (don't show per-seat price)
      if (flatAmount > 0 && unitAmount === 0) {
        return null;
      }
      // If tier has both, show combined effective rate
      if (flatAmount > 0 && unitAmount > 0) {
        return ((flatAmount / seatCount) + unitAmount) / 100;
      }
      // Standard unit_amount only
      return unitAmount / 100;
    }
  }
  return null;
};

/**
 * Get tier breakdown for graduated pricing display.
 * Returns an array of tier breakdowns showing seats and price for each tier used.
 * Returns null for volume pricing or when only one tier is used (use simple display instead).
 *
 * @param tiers - Array of Stripe tier objects
 * @param seatCount - Number of seats/units
 * @param tiersMode - 'volume' or 'graduated'
 * @returns Array of tier breakdowns, or null
 */
export const getTierBreakdown = (tiers: ?Array<Object>, seatCount: number, tiersMode: ?string): ?Array<TierBreakdown> => {
  // Return null for non-graduated pricing (volume mode charges all at single rate)
  if (!tiers || tiers.length === 0 || seatCount <= 0 || tiersMode === 'volume') return null;

  const breakdowns: Array<TierBreakdown> = [];
  let remaining = seatCount;
  let prevUpTo = 0;

  for (const tier of tiers) {
    if (remaining <= 0) break;

    const tierMax = tier.up_to === null ? remaining : tier.up_to - prevUpTo;
    const unitsInTier = Math.min(remaining, tierMax);
    const flatAmount = tier.flat_amount || 0;
    const unitAmount = tier.unit_amount || 0;

    // Calculate subtotal for this tier
    const subtotal = (flatAmount + (unitsInTier * unitAmount)) / 100;

    // Determine if this is a flat-only tier
    const isFlat = flatAmount > 0 && unitAmount === 0;

    breakdowns.push({
      seats: unitsInTier,
      pricePerSeat: isFlat ? null : unitAmount / 100,
      subtotal,
      isFlat
    });

    remaining -= unitsInTier;
    prevUpTo = tier.up_to || 0;
  }

  // Return null if only one tier is used (use simple display instead)
  if (breakdowns.length <= 1) return null;

  return breakdowns;
};

/**
 * Calculate display price for a plan, handling both flat-rate and tiered pricing.
 *
 * @param planInfo - Stripe plan object
 * @param quantity - Number of seats/units (defaults to 1)
 * @returns Price in dollars ready for display
 */
export const getDisplayPrice = (planInfo: ?Object, quantity: number = 1): number => {
  if (!planInfo) return 0;

  // Check if this is tiered pricing
  if (planInfo.billing_scheme === 'tiered' && planInfo.tiers) {
    return calculateTieredPrice(planInfo.tiers, quantity, planInfo.tiers_mode);
  }

  // Flat-rate pricing: use amount or unit_amount
  return (planInfo.amount || planInfo.unit_amount || 0) / 100;
};
